import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from '@revelio/core/composition-buckets';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { CompositionRawDataProvider } from '../../DataProvider/composition/CompositionRawDataProvider';
import {
  type CompositionV3AnyGraphqlResponse,
  type CompositionV3FullMetrics,
  isCompositionV3FullMetrics,
} from '../../DataProvider/composition/composition-v3/composition-v3-graphql-response';
import {
  DataProviderQueryStatus,
  DataType,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../../DataProvider/types';
import type { ResolvedData } from '../../DataResolver/resolve-items';
import type { CompositionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import { usePageState } from '../../PageState/PageStateProvider';
import type { SubfilterTree } from '../../PageState/types';
import { Subfilter } from '../../Subfilter/Subfilter';
import { ChartError } from '../ChartError';
import { ChartFallback } from '../ChartFallback';
import { ChartLoader } from '../ChartLoader';
import { useChartMenuContext } from '../chart-menu/ChartMenuContext';
import { Line } from '../Line';
import styles from './ShareOfSegment.module.css';
import {
  getSegmentLeafId,
  getTableSegments,
  normalizeSegmentName,
  tableToTimeSeriesData,
} from './share-of-segment-table';
import { useShareOfSegmentSubfilters } from './useSubFilter';

type ShareOfSegmentProps = {
  subfilterList: SubfilterTree[];
  selectedSegments: SubfilterTree[];
  chartData: TimeseriesDataSet | null;
  onSubmitSubfilter: (selections: SubfilterTree[]) => void;
};

const EMPTY_SELECTED_SEGMENTS: SubfilterTree[] = [];

type SegmentSelectionControls = {
  selectedSegments: SubfilterTree[];
  setSelections: (
    selections: SubfilterTree[],
    options?: { updateInitial?: boolean; fromChartSwitcher?: boolean }
  ) => void;
};

export const useSegmentSelections = (
  plotId: string
): SegmentSelectionControls => {
  const { pageState, dispatch } = usePageState();
  const selectedSegments =
    pageState.selections?.[plotId] ?? EMPTY_SELECTED_SEGMENTS;

  const setSelections = useCallback(
    (
      selections: SubfilterTree[],
      {
        updateInitial = false,
        fromChartSwitcher = false,
      }: { updateInitial?: boolean; fromChartSwitcher?: boolean } = {}
    ) => {
      dispatch({
        type: 'SET_SELECTIONS',
        payload: {
          plotId,
          selections,
          updateInitial,
          fromChartSwitcher,
        },
      });
    },
    [dispatch, plotId]
  );

  return { selectedSegments, setSelections };
};

const ShareOfSegment = ({
  subfilterList,
  selectedSegments,
  chartData,
  onSubmitSubfilter,
}: ShareOfSegmentProps) => {
  const { setDataProviderSet } = useChartMenuContext();

  useEffect(() => {
    if (chartData && chartData.data.length > 0 && selectedSegments.length > 0) {
      setDataProviderSet(chartData);
    }
  }, [chartData, selectedSegments, setDataProviderSet]);

  return (
    <div className={styles.chartRoot}>
      <Subfilter
        subfilterList={subfilterList}
        selectedItems={selectedSegments}
        onSubmit={onSubmitSubfilter}
        className={styles.subfilterTrigger}
        data-export-hidden
        openByDefault={subfilterList.some(
          (item) => item?.children && item.children.length > 0
        )}
      />
      <Line
        data={chartData?.data ?? []}
        metricOverride="share_of_headcount"
        selectedSegments={selectedSegments}
      />
    </div>
  );
};

const ShareOfSegmentChartShell = ({
  status,
  haveSelectionsBeenModified,
  children,
}: {
  status: DataProviderQueryStatus;
  haveSelectionsBeenModified?: boolean;
  children: ReactNode;
}) => {
  if (haveSelectionsBeenModified) {
    const shouldShowLoader = [
      DataProviderQueryStatus.Idle,
      DataProviderQueryStatus.Fetching,
    ].includes(status);

    return (
      <div className={styles.statusRoot}>
        {!shouldShowLoader && children}
        {status === DataProviderQueryStatus.Error ? <ChartError /> : null}
        {shouldShowLoader ? <ChartLoader /> : null}
      </div>
    );
  }

  return <ChartFallback status={status}>{children}</ChartFallback>;
};

export const areSelectionsValid = (
  selectedSegments: SubfilterTree[],
  subfilterList: SubfilterTree[],
  isTreeSubfilter: boolean
) => {
  if (!selectedSegments.length) return false;
  if (!subfilterList.length) return true;

  const comparisonList = isTreeSubfilter
    ? subfilterList.flatMap((item) =>
        item.children && item.children.length > 0 ? item.children : [item]
      )
    : subfilterList;

  if (!comparisonList.length) return false;

  const comparisonIds = new Set(
    comparisonList
      .map((item) => getSegmentLeafId(item.id))
      .filter((id): id is string => id !== null)
  );
  const comparisonNames = new Set(
    comparisonList
      .map((item) => normalizeSegmentName(item.name))
      .filter((name): name is string => name !== null)
  );

  return selectedSegments.every((segment) => {
    const leafId = getSegmentLeafId(segment?.id);
    if (leafId !== null && comparisonIds.has(leafId)) return true;
    const name = normalizeSegmentName(segment?.name);
    return name !== null && comparisonNames.has(name);
  });
};

type SetSegmentSelections = (
  selections: SubfilterTree[],
  options?: { updateInitial?: boolean }
) => void;

type ShareOfSegmentChartBodyProps = {
  baseSubfilterList: SubfilterTree[];
  isTreeSubfilter: boolean;
  onSubmitSubfilter: (selections: SubfilterTree[]) => void;
  secondaryDimension: CompositionSecondaryDimensionValues;
  selectedSegments: SubfilterTree[];
  setSelections: SetSegmentSelections;
};

export const ShareOfSegmentChart = ({
  primaryDimension,
  secondaryDimension,
  plotId,
  data: resolvedData,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: CompositionSecondaryDimensionValues;
  plotId: string;
  data?: ResolvedData;
}) => {
  const { selectedSegments, setSelections } = useSegmentSelections(plotId);
  const { subfilterList, isTreeSubfilter } =
    useShareOfSegmentSubfilters(secondaryDimension);

  const handleSubmitSubfilter = useCallback(
    (selections: SubfilterTree[]) => setSelections(selections),
    [setSelections]
  );

  const bodyProps: ShareOfSegmentChartBodyProps = {
    baseSubfilterList: subfilterList,
    isTreeSubfilter,
    onSubmitSubfilter: handleSubmitSubfilter,
    secondaryDimension,
    selectedSegments,
    setSelections,
  };

  if (resolvedData) {
    return (
      <ResolvedShareOfSegmentChartBody
        {...bodyProps}
        resolvedData={resolvedData}
      />
    );
  }

  // The exhaustive breakdown IDs needed for this chart's segment dropdown
  // are derived inside `CompositionV3BreakdownDataProvider` from the page
  // config so the breakdown query for every chart on the tab shares the
  // same variables (URQL dedupes them to a single request).
  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      isTimeseries
      getSelectionBreakdowns
    >
      {({ data, status, haveSelectionsBeenModified, monthRefs = [] }) => (
        <ShareOfSegmentChartBodyV3
          {...bodyProps}
          data={data?.composition_v3 ?? undefined}
          haveSelectionsBeenModified={haveSelectionsBeenModified}
          monthRefs={monthRefs}
          status={status}
        />
      )}
    </CompositionRawDataProvider>
  );
};

const ResolvedShareOfSegmentChartBody = ({
  resolvedData: { data: table, status },
  ...props
}: ShareOfSegmentChartBodyProps & { resolvedData: ResolvedData }) => {
  const { secondaryDimension, selectedSegments } = props;
  const responseSubfilterList = useMemo(() => {
    if (table === undefined) return undefined;
    return shouldUseResponseBackedSubfilters(secondaryDimension)
      ? getTableSegments(table)
      : [];
  }, [table, secondaryDimension]);
  const chartData = useMemo(
    () =>
      table === undefined
        ? null
        : tableToTimeSeriesData(table, selectedSegments),
    [table, selectedSegments]
  );

  return (
    <ShareOfSegmentChartBody
      {...props}
      chartData={chartData}
      responseSubfilterList={responseSubfilterList}
      status={status}
    />
  );
};

const ShareOfSegmentChartBodyV3 = ({
  data,
  haveSelectionsBeenModified,
  monthRefs,
  status,
  ...props
}: ShareOfSegmentChartBodyProps & {
  data: CompositionV3AnyGraphqlResponse | undefined;
  haveSelectionsBeenModified?: boolean;
  monthRefs: string[];
  status: DataProviderQueryStatus;
}) => {
  const { secondaryDimension, selectedSegments } = props;
  const responseSubfilterList = useMemo(
    () =>
      data === undefined
        ? undefined
        : buildResponseSubfilterListV3(data, secondaryDimension),
    [data, secondaryDimension]
  );
  const chartData = buildShareOfSegmentChartDataV3(
    data,
    secondaryDimension,
    selectedSegments,
    monthRefs
  );

  return (
    <ShareOfSegmentChartBody
      {...props}
      chartData={chartData}
      haveSelectionsBeenModified={haveSelectionsBeenModified}
      responseSubfilterList={responseSubfilterList}
      status={status}
    />
  );
};

const ShareOfSegmentChartBody = ({
  baseSubfilterList,
  chartData,
  haveSelectionsBeenModified,
  isTreeSubfilter,
  onSubmitSubfilter,
  responseSubfilterList,
  selectedSegments,
  setSelections,
  status,
}: ShareOfSegmentChartBodyProps & {
  chartData: TimeseriesDataSet | null;
  haveSelectionsBeenModified?: boolean;
  /** The segments in the data, until it next loads. */
  responseSubfilterList: SubfilterTree[] | undefined;
  status: DataProviderQueryStatus;
}) => {
  const lastResponseSubfilterListRef = useRef<SubfilterTree[]>([]);
  if (responseSubfilterList !== undefined) {
    lastResponseSubfilterListRef.current = responseSubfilterList;
  }
  const shownResponseSubfilterList =
    responseSubfilterList ?? lastResponseSubfilterListRef.current;
  const resolvedSubfilterList =
    shownResponseSubfilterList.length > 0
      ? shownResponseSubfilterList
      : baseSubfilterList;
  const resolvedIsTreeSubfilter =
    shownResponseSubfilterList.length > 0 ? false : isTreeSubfilter;
  const resolvedDefaultSelection = useMemo(
    () => getDefaultSegmentSelection(resolvedSubfilterList),
    [resolvedSubfilterList]
  );

  useEffect(() => {
    if (!resolvedSubfilterList.length || !resolvedDefaultSelection) return;

    const hasSelections = selectedSegments.length > 0;
    const selectionsValid = hasSelections
      ? areSelectionsValid(
          selectedSegments,
          resolvedSubfilterList,
          resolvedIsTreeSubfilter
        )
      : false;

    if (!hasSelections || !selectionsValid) {
      setSelections([resolvedDefaultSelection], { updateInitial: true });
    }
  }, [
    resolvedSubfilterList,
    resolvedDefaultSelection,
    resolvedIsTreeSubfilter,
    selectedSegments,
    setSelections,
  ]);

  return (
    <ShareOfSegmentChartShell
      haveSelectionsBeenModified={haveSelectionsBeenModified}
      status={status}
    >
      <ShareOfSegment
        chartData={chartData}
        onSubmitSubfilter={onSubmitSubfilter}
        selectedSegments={selectedSegments}
        subfilterList={resolvedSubfilterList}
      />
    </ShareOfSegmentChartShell>
  );
};

const shouldUseResponseBackedSubfilters = (
  secondaryDimension: CompositionSecondaryDimensionValues
) =>
  secondaryDimension !== 'gender' &&
  secondaryDimension !== 'ethnicity' &&
  secondaryDimension !== 'education';

const fullMetricsForShare = (
  entity: NonNullable<CompositionV3AnyGraphqlResponse['entities']>[number]
): CompositionV3FullMetrics | undefined =>
  isCompositionV3FullMetrics(entity.metrics) ? entity.metrics : undefined;

const buildResponseSubfilterListV3 = (
  data: CompositionV3AnyGraphqlResponse | undefined,
  secondaryDimension: CompositionSecondaryDimensionValues
): SubfilterTree[] => {
  if (!data?.entities) return [];
  if (!shouldUseResponseBackedSubfilters(secondaryDimension)) return [];

  const itemsById = new Map<string, SubfilterTree>();
  data.entities.forEach((entity) => {
    const metricData = fullMetricsForShare(entity)?.[secondaryDimension];
    metricData?.forEach((segment) => {
      if (isEmptyCompositionBucket(segment.id)) return;
      const id = `${segment.id}`;
      if (itemsById.has(id)) return;
      const name = isOtherCompositionBucket(segment.id)
        ? OTHER_BUCKET_LABEL
        : segment.short_name || segment.long_name || id;
      itemsById.set(id, { id, name });
    });
  });

  return Array.from(itemsById.values());
};

const getFirstValidChild = (
  children?: SubfilterTree[]
): SubfilterTree | null => {
  if (!children) return null;
  const validChild = children.find((child) => {
    if (!child?.id || !child?.name) return false;
    return true;
  });
  return validChild ? { id: validChild.id, name: validChild.name } : null;
};

const getDefaultSegmentSelection = (
  subfilterList: SubfilterTree[]
): SubfilterTree | null => {
  for (const item of subfilterList) {
    const firstNestedChild = getFirstValidChild(item.children);
    if (firstNestedChild) {
      return firstNestedChild;
    }

    const isSubFilterTreeWithoutGlobalFilter =
      item.id && item.name && !item.children?.length;
    if (isSubFilterTreeWithoutGlobalFilter) {
      return { id: item.id, name: item.name };
    }
  }
  return null;
};

const buildShareOfSegmentChartDataV3 = (
  data: CompositionV3AnyGraphqlResponse | undefined,
  secondaryDimension: CompositionSecondaryDimensionValues,
  selectedSegments: SubfilterTree[],
  monthRefs: string[]
): TimeseriesDataSet | null => {
  if (!data?.entities || selectedSegments.length === 0) return null;

  const selectedSegmentIds = new Set(
    selectedSegments
      .map((segment) => getSegmentLeafId(segment?.id))
      .filter((id): id is string => Boolean(id))
  );
  const selectedSegmentNames = new Set(
    selectedSegments
      .map((segment) => normalizeSegmentName(segment?.name))
      .filter((name): name is string => Boolean(name))
  );

  const compositionData: TimeseriesDataSet['data'] =
    data.entities?.map((entity) => {
      const dim = entity.short_name;
      const metricData = fullMetricsForShare(entity)?.[secondaryDimension];
      const selectedMetrics =
        metricData?.filter((segment) => {
          if (isEmptyCompositionBucket(segment.id)) return false;
          if (selectedSegmentIds.has(`${segment.id}`)) {
            return true;
          }

          const shortName = normalizeSegmentName(segment.short_name);
          const longName = normalizeSegmentName(segment.long_name);
          return (
            (shortName !== null && selectedSegmentNames.has(shortName)) ||
            (longName !== null && selectedSegmentNames.has(longName))
          );
        }) ?? [];

      if (!selectedMetrics.length) {
        return { dim, timeseries: [] };
      }

      const timeseries: TimeseriesDataPoint[] = [];
      const loopBound = Math.max(
        ...selectedMetrics.map((m) => m.headcount?.length ?? 0)
      );

      for (
        let timePointIndex = 0;
        timePointIndex < loopBound;
        timePointIndex++
      ) {
        for (
          let metricIndex = 0;
          metricIndex < selectedMetrics.length;
          metricIndex++
        ) {
          const row = selectedMetrics[metricIndex];
          const timePoint = row?.headcount?.[timePointIndex];
          if (!timePoint) continue;

          const share = timePoint.share ?? 0;
          const count = timePoint.count ?? 0;
          const date = monthRefs[timePoint.id];
          if (!date) continue;

          const existing = timeseries[timePointIndex];
          if (existing) {
            existing.value =
              (typeof existing.value === 'number' ? existing.value : 0) + share;
            existing.secondaryValue =
              (typeof existing.secondaryValue === 'number'
                ? existing.secondaryValue
                : 0) + count;
          } else {
            timeseries[timePointIndex] = {
              date,
              value: share,
              secondaryValue: count,
            };
          }
        }
      }

      return {
        dim,
        timeseries: timeseries.map((point) => ({
          ...point,
          value:
            typeof point.value === 'number'
              ? Math.round(point.value * 10000) / 10000
              : point.value,
        })),
      };
    }) ?? [];

  return { type: DataType.TIMESERIES, data: compositionData };
};
